import Sentry from '../Sentry';

// Path form is canonical; ?source_id= is kept for older shared links. The source's ingest
// URL exposes the id with the `src_` prefix stripped (e.g. /abc123), so we accept a bare
// segment and reconstruct the prefixed id — the session endpoint expects `src_…`.
// The optional `e/` prefix is the ingestion path on the console hostname (/e/<id>): the
// edge middleware redirects browser navigations to the un-prefixed path, but the app also
// handles /e/<id> directly so a stale link still resolves (and so local dev works without
// the middleware).
const SOURCE_ID_PATH_PATTERN = /^\/(?:e\/)?(src_[A-Za-z0-9]+)\/?$/;
// Source IDs are 14 lowercase-alphanumeric chars (after the stripped `src_`); the {10,}
// floor keeps the match from grabbing short static routes (e.g. /new, /docs) as bogus IDs.
const STRIPPED_SOURCE_ID_PATH_PATTERN = /^\/(?:e\/)?([A-Za-z0-9]{10,})\/?$/;

let cached_source_id: string | null | undefined;

/** Clears the in-memory deep-link cache (for unit tests). */
export const resetDeepLinkSourceIdCacheForTests = (): void => {
  cached_source_id = undefined;
};

export const getDeepLinkSourceId = (): string | null => {
  if (cached_source_id !== undefined) {
    return cached_source_id;
  }

  try {
    const path_match = window.location.pathname.match(SOURCE_ID_PATH_PATTERN);
    if (path_match) {
      cached_source_id = path_match[1];
      return cached_source_id;
    }

    const stripped_match = window.location.pathname.match(STRIPPED_SOURCE_ID_PATH_PATTERN);
    if (stripped_match) {
      cached_source_id = `src_${stripped_match[1]}`;
      return cached_source_id;
    }

    const params = new URLSearchParams(window.location.search);
    cached_source_id = params.get('source_id');
    return cached_source_id;
  } catch {
    cached_source_id = null;
    return null;
  }
};

/**
 * Strip the legacy `?source_id=` query param once it has been read. That is all it does —
 * it no longer touches the path, which is why it is not called `cleanDeepLinkFromUrl` any
 * more.
 *
 * The **path** form is deliberately left in place: `/{id}` is the canonical inspect URL,
 * so it has to survive a reload, a bookmark, and the back button. Rewriting it to `/`
 * (as this used to) meant a refresh resolved the session with no `source_id`, dropped the
 * shared sandbox, and silently provisioned an unrelated Source in the visitor's own one.
 * `syncDeepLinkToSource` keeps the path pointed at whichever Source is on screen instead.
 */
export const stripLegacySourceIdParam = (): void => {
  try {
    if (!window.location.search) {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    if (!params.has('source_id')) {
      return;
    }

    params.delete('source_id');
    const new_params_string = params.toString();
    let url = window.location.pathname;
    if (new_params_string.length > 0) {
      url += `?${new_params_string}`;
    }
    window.history.replaceState({}, document.title, url);
  } catch (e) {
    Sentry.captureException(e);
  }
};

/**
 * Point the address bar at `source_id`'s inspect path, so the URL always names the Source
 * on screen — reload, copy-paste and share all keep working after a Source switch.
 */
export const syncDeepLinkToSource = (source_id: string): void => {
  try {
    const path = `/${source_id.replace(/^src_/, '')}`;
    if (window.location.pathname === path) {
      return;
    }
    cached_source_id = source_id;
    window.history.replaceState({}, document.title, `${path}${window.location.search}`);
  } catch (e) {
    Sentry.captureException(e);
  }
};
